// Global Instructions Rule Applied!
//
// Prospect States strip (LP agent programme Phase 4b): the prospect-level
// twin of DealStatesStrip, bound to the prospect tracker's catalogue —
// interest / objections / decision process, the dated deadline, open asks.
// The agent-maintained overlay on the human-owned pipeline status: the strip
// never shows or edits the stage. The server filters by agent_type_key.
import React, { useCallback } from 'react'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  createTaskFromProspectState,
  fetchProspectStates,
  overrideProspectState
} from '../services/internalAgentsService.ts'
import { ProspectState } from '../../../state_manager/data_models/internal_agents_model.ts'
import { StatesStrip } from './StatesStrip.tsx'
import { StateItem } from './StateCard.tsx'

const PROSPECT_AGENT_TYPE_KEY = 'prospect_tracker'

interface ProspectStatesStripProps {
  prospectId: string
  title?: string
  /** Pre-selects assignees in the create-task form (the account's primary
   *  relationship manager when the host has it). Never auto-assigns. */
  defaultAssignees?: Array<{ label: string; value: string }>
  /** Fired after a task is created so the host can refresh its Tasks panel. */
  onTaskCreated?: () => void
  /** Hands `load` to the prospect form so its Refresh button reloads this panel. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

const toStateItem = (s: ProspectState): StateItem => ({
  id: s.prospect_states__id,
  stateKey: s.prospect_state_types__state_key,
  label: s.prospect_state_types__label,
  kind: s.prospect_state_types__kind,
  conditionKey: s.prospect_states__condition_key,
  conditionLabel: s.condition_label,
  conditionRank: s.condition_rank,
  summary: s.prospect_states__summary,
  status: s.prospect_states__status,
  startedAt: s.prospect_states__started_at,
  lastConfirmedAt: s.prospect_states__last_confirmed_at,
  lastEvidenceAt: s.prospect_states__last_evidence_at ?? null,
  lastCheckedAt: s.prospect_states__last_checked_at ?? null,
  expectedEndAt: s.prospect_states__expected_end_at ?? null,
  expectedEndBasis: s.prospect_states__expected_end_basis ?? null,
  userSet: s.prospect_states__user_set,
  conditions: s.conditions ?? [],
  taskId: s.prospect_states__task_id ?? null,
  taskSubject: s.tasks__subject ?? null,
  taskCompleted: s.tasks__completed ?? null,
  taskDueBy: s.tasks__due_by ?? null
})

export const ProspectStatesStrip: React.FC<ProspectStatesStripProps> = ({
  prospectId,
  title = 'Current State',
  defaultAssignees = [],
  onTaskCreated,
  onRefreshReady = null
}) => {
  const token = () => getAccessToken(store.getState().auth)

  const fetchStates = useCallback(
    async () =>
      (await fetchProspectStates(prospectId, PROSPECT_AGENT_TYPE_KEY, token())).map(toStateItem),
    [prospectId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const overrideState = useCallback(
    async (args: {
      stateKey: string
      action: 'set_condition' | 'edit_summary' | 'close'
      stateId?: string | null
      conditionKey?: string | null
      summary?: string | null
    }) =>
      overrideProspectState(
        {
          prospect_id: prospectId,
          state_key: args.stateKey,
          action: args.action,
          state_id: args.stateId,
          condition_key: args.conditionKey,
          summary: args.summary
        },
        token()
      ),
    [prospectId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const createTask = useCallback(
    async (stateId: string, taskRecord: unknown) =>
      createTaskFromProspectState(stateId, taskRecord, token()),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <StatesStrip
      title={title}
      fetchStates={fetchStates}
      overrideState={overrideState}
      createTaskFromState={createTask}
      defaultAssignees={defaultAssignees}
      onTaskCreated={onTaskCreated}
      subjectNoun='prospect'
      collapseKey='prospect_dashboard'
      defaultCollapsed={false}
      onRefreshReady={onRefreshReady}
    />
  )
}
